import React, { useState, useMemo } from "react";
import { useQuery } from "react-query";
import { Button, Form, Alert, Card, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { SearchAlt } from "@styled-icons/boxicons-regular/SearchAlt";
import Spinner from "@components/Spinner/Spinner";
import ReactTable from "@components/ReactTable";
import { getCustomerSensors } from "../services/sensorCrud";

export default function CustomerLookupTab({ onSelectSensor }) {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language || "de";
  const timeString =
    lang === "de" ? "de-DE" : lang === "en" ? "en-UK" : lang === "it" ? "it-IT" : "de-DE";

  const [cloudIdInput, setCloudIdInput] = useState("");
  const [searchCloudId, setSearchCloudId] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState([]);

  const {
    data: customerData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ["customerSensors", { cNamePart: searchCloudId }],
    () => getCustomerSensors({ cNamePart: searchCloudId }),
    {
      enabled: !!searchCloudId,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const renderError = () => {
    if (error?.response?.status === 404) {
      return (
        <Alert variant="warning" className="mt-3">
          <strong>{t("customer_not_found") || "Customer not found"}</strong>
          <br />
          {t("customer_not_found_message") ||
            "No customer could be found with the provided Cloud ID."}
        </Alert>
      );
    }
    const errMessage =
      error?.response?.data?.message || error?.message || t("lorawan_keys_fetch_failed");
    return (
      <Alert variant="danger" className="mt-3">
        <strong>{t("error_label") || "Error"}:</strong> {errMessage}
      </Alert>
    );
  };

  const sensors = Array.isArray(customerData?.sensors) ? customerData.sensors : [];

  const uniqueSensors = useMemo(() => {
    const seen = new Set();
    return sensors.filter((sensor) => {
      const identifier = sensor.imei || sensor.hardware_serial;
      if (!identifier || seen.has(identifier)) return false;
      seen.add(identifier);
      return true;
    });
  }, [sensors]);

  const hasLWSensors = uniqueSensors.some((s) => s.network_type === "LW");
  const pageSize = 20;

  const sortedSensors = useMemo(() => {
    if (!sortBy.length) return uniqueSensors;
    const { id, desc } = sortBy[0];
    return [...uniqueSensors].sort((a, b) => {
      let valA = a[id];
      let valB = b[id];

      if (id === "sensor_events") {
        const getLatest = (events) =>
          events && events.length > 0
            ? Math.max(...events.map((e) => new Date(e.timestamp).getTime()))
            : 0;
        valA = getLatest(a.sensor_events);
        valB = getLatest(b.sensor_events);
      } else if (id === "hardware_serial") {
        valA = a.hardware_serial || a.imei || "";
        valB = b.hardware_serial || b.imei || "";
        if (valA.startsWith("F")) valA = valA.slice(1);
        if (valB.startsWith("F")) valB = valB.slice(1);
      }

      if (valA === valB) return 0;
      const comp = valA > valB ? 1 : -1;
      return desc ? -comp : comp;
    });
  }, [uniqueSensors, sortBy]);

  const pageCount = Math.ceil(sortedSensors.length / pageSize);
  const paginatedSensors = sortedSensors.slice((page - 1) * pageSize, page * pageSize);

  const columns = useMemo(() => {
    const cols = [
      {
        id: "select_action",
        Header: "",
        disableSortBy: true,
        style: { width: "45px", textAlign: "center" },
        Cell: ({ row }) => {
          const imei = row.original.imei || row.original.hardware_serial;
          return (
            <div className="text-center">
              <Button
                variant="light"
                size="sm"
                className="p-1 border d-inline-flex align-items-center justify-content-center"
                style={{ cursor: "pointer" }}
                title={t("select_sensor") || "Select Sensor"}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onSelectSensor && imei) {
                    onSelectSensor(imei);
                  }
                }}
              >
                <SearchAlt size="16px" />
              </Button>
            </div>
          );
        },
      },
      {
        Header: t("hardware_serial_imei"),
        accessor: "hardware_serial",
        Cell: ({ value, row }) => {
          const val = value || row.original.imei;
          return val?.startsWith("F") ? val.slice(1) : val;
        },
      },
    ];

    if (hasLWSensors) {
      cols.push(
        { Header: t("join_eui"), accessor: "join_eui" },
        { Header: t("app_key"), accessor: "app_key" }
      );
    }

    cols.push(
      {
        Header: t("sensor_type"),
        accessor: "sensor_type",
        Cell: ({ value, row }) => {
          if (value === "pulse") {
            const networkType = row.original.network_type;
            const hwSerial = row.original.hardware_serial || "";
            if (networkType === "NB") return "Pulse N";
            if (networkType === "LW") {
              if (hwSerial.startsWith("A")) return "Pulse R";
              if (hwSerial.startsWith("0")) return "Pulse S";
            }
            return "Pulse";
          }
          if (value === "soil_moisture") return "FDR";
          if (value === "dendrometer") return "Dendrometer";
          if (value === "soil_water_tension") {
            const model = row.original.soil_water_tension_sensor?.model;
            if (model === "WM") return "Watermark";
            if (model === "TE") return "Teros21";
            return "Watermark / Teros21";
          }
          return value || "";
        },
      },
      { Header: t("network_type"), accessor: "network_type" },
      {
        Header: t("last_sensor_event") || "Last Sensor Event",
        accessor: "sensor_events",
        Cell: ({ value }) => {
          if (!value || value.length === 0) return "-";
          const latest = value.reduce(
            (max, event) => (new Date(event.timestamp) > new Date(max.timestamp) ? event : max),
            value[0]
          );
          return new Date(latest.timestamp).toLocaleString(timeString, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
      }
    );

    return cols;
  }, [t, hasLWSensors, timeString, onSelectSensor]);

  const handleFetch = (e) => {
    e.preventDefault();
    if (!cloudIdInput.trim()) return;
    setSearchCloudId(cloudIdInput.trim());
    setPage(1);
    refetch();
  };

  const customerName = customerData?.customer_name || "";
  const customerId = customerData?.customer_id || "";
  const packageName = customerData?.package_name || "";

  return (
    <Card>
      <Card.Body>
        <h5>{t("customer_details")}</h5>
        <Form onSubmit={handleFetch}>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>{t("customer_name_or_cloud_id")}</Form.Label>
                <Form.Control
                  type="text"
                  value={cloudIdInput}
                  onChange={(e) => setCloudIdInput(e.target.value)}
                  placeholder={t("enter_customer_name_or_cloud_id")}
                  disabled={isLoading}
                  className="mb-4"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-center">
              <Form.Group className="w-100">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isLoading || !cloudIdInput.trim()}
                >
                  {isLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t("fetching")}
                    </>
                  ) : (
                    t("fetch")
                  )}
                </Button>
              </Form.Group>
            </Col>
          </Row>
        </Form>

        {isError && renderError()}

        {customerData && (
          <div className="mt-4">
            <h6 className="mb-3 ms-0">{t("customer_info")}</h6>
            <Row className="mb-4">
              {customerName && (
                <Col sm={6} md={3} className="mb-2">
                  <span className="text-muted d-block small fw-bold">{t("name")}</span>
                  <span className="fw-semibold">{customerName}</span>
                </Col>
              )}
              {customerId && (
                <Col sm={6} md={3} className="mb-2">
                  <span className="text-muted d-block small fw-bold">{t("customer_id")}</span>
                  <span className="fw-semibold">{customerId}</span>
                </Col>
              )}
              {packageName && (
                <Col sm={6} md={3} className="mb-2">
                  <span className="text-muted d-block small fw-bold">{t("package")}</span>
                  <span className="fw-semibold">{packageName}</span>
                </Col>
              )}
              <Col sm={6} md={3} className="mb-2">
                <span className="text-muted d-block small fw-bold">{t("total_probes")}</span>
                <span className="fw-semibold">{sensors.length}</span>
              </Col>
              <Col sm={6} md={3} className="mb-2">
                <span className="text-muted d-block small fw-bold">{t("total_sensors")}</span>
                <span className="fw-semibold">{uniqueSensors.length}</span>
              </Col>
            </Row>

            {uniqueSensors.length > 0 ? (
              <>
                <h6>{t("customer_sensors_list")}</h6>
                <ReactTable
                  key={`customer-sensors-${uniqueSensors.length}`}
                  columns={columns}
                  data={paginatedSensors}
                  pagesize={pageSize}
                  manualPagination
                  manualSortBy
                  onSortChange={setSortBy}
                  pageCount={pageCount}
                  currentPage={page}
                  onPageChange={(newPage) => setPage(newPage)}
                  compact={true}
                  filter={false}
                />
              </>
            ) : (
              <Alert variant="warning">{t("no_sensors_found")}</Alert>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
